import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import type { Entry } from '../../../features/admin/modules/modules.model';

interface DialogData {
  entry: Entry;
  title: string;
}

interface CompetencyItem {
  name: string;
  description?: string;
  role?: string;
  archived?: boolean;
}

interface InformationItem {
  label?: string;
  key: string;
  value: any;
  icon?: string;
}

@Component({
  selector: 'app-user-detail-modal',
  template: `
    <div class="flex flex-col max-h-[85vh]">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 pb-4">
        <h2 class="text-2xl font-bold text-foreground">Profiel Details</h2>
        <button
          mat-icon-button
          (click)="close()"
          class="text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto px-6 pb-6">
        <!-- Profile Header Card -->
        <div class="bg-card rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6 mb-6">
          <div class="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
            <!-- Profile Image and Basic Info (Mobile: vertical, Desktop: horizontal) -->
            <div class="flex items-start gap-4">
              @if (data.entry.uiState.cardInfo?.image) {
                <div class="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden shadow-md">
                  <img
                    [src]="data.entry.uiState.cardInfo?.image"
                    alt="profiel foto"
                    class="w-full h-full object-cover" />
                </div>
              } @else {
                <div
                  class="bg-gray-100 dark:bg-gray-700 rounded-lg w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center shadow-md">
                  <mat-icon
                    class="icon-size-10 sm:icon-size-12 text-gray-400 dark:text-gray-300"
                    svgIcon="feather:user"></mat-icon>
                </div>
              }

              <!-- Name and basic info -->
              <div class="flex-1 min-w-0 sm:hidden">
                <h3 class="text-lg font-bold text-foreground mb-1">{{ data.entry.uiState.cardInfo?.title }}</h3>
                <!-- Rating info (Mobile) -->
                @if (getRatingInfo(); as rating) {
                  <div class="flex items-center gap-2 text-sm">
                    <div class="flex items-center gap-1">
                      <mat-icon class="text-yellow-500 !text-base">star</mat-icon>
                      <span class="font-semibold text-gray-900">{{ rating.rating }}</span>
                    </div>
                    @if (rating.amount !== '-') {
                      <span class="text-gray-500">({{ rating.amount }})</span>
                    }
                  </div>
                }
              </div>
            </div>

            <!-- Desktop: Profile Info and Competencies -->
            <div class="flex-1 min-w-0 hidden sm:block">
              <h3 class="text-xl font-bold text-foreground mb-2">{{ data.entry.uiState.cardInfo?.title }}</h3>

              <!-- Competencies as chips -->
              @if (getCompetenciesInfo(); as competencies) {
                <div class="flex flex-wrap gap-1.5 mb-3">
                  @for (competency of competencies.slice(0, 4); track competency.name) {
                    <mat-chip class="!text-xs !py-1 !px-2 !h-6 ">
                      {{ competency.name }}
                    </mat-chip>
                  }
                  @if (competencies.length > 4) {
                    <span class="text-xs text-gray-500 px-2 py-1">+{{ competencies.length - 4 }} meer</span>
                  }
                </div>
              } @else if (data.entry.uiState.cardInfo?.subTitle) {
                <p class="text-gray-600 text-sm mb-3">{{ data.entry.uiState.cardInfo?.subTitle }}</p>
              }

              <!-- Rating info (Desktop) -->
              @if (getRatingInfo(); as rating) {
                <div class="flex items-center gap-2 text-sm">
                  <div class="flex items-center gap-1">
                    <mat-icon class="text-yellow-500 !text-base">star</mat-icon>
                    <span class="font-semibold">{{ rating.rating }}</span>
                  </div>
                  @if (rating.amount !== '-') {
                    <span>({{ rating.amount }} beoordelingen)</span>
                  } @else {
                    <span>(Geen beoordelingen)</span>
                  }
                </div>
              }
            </div>

            <!-- Rate info (both mobile and desktop) -->
            @if (getRateInfo(); as rate) {
              <div class="flex-shrink-0 text-right sm:text-right">
                <!-- Data value, not an accent affordance: neutral Foreground/Default (Figma). -->
                <div class="text-lg font-bold">€{{ rate }}</div>
                <div class="text-xs text-gray-500">per uur</div>
              </div>
            }
          </div>

          <!-- Mobile: Competencies below header -->
          @if (getCompetenciesInfo(); as competencies) {
            <div class="sm:hidden mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div class="flex flex-wrap gap-1.5">
                @for (competency of competencies.slice(0, 6); track competency.name) {
                  <mat-chip class="!text-xs !py-1 !px-2 !h-6 bg-blue-50 text-blue-700 border-blue-200">
                    {{ competency.name }}
                  </mat-chip>
                }
                @if (competencies.length > 6) {
                  <span class="text-xs text-gray-500 px-2 py-1">+{{ competencies.length - 6 }} meer</span>
                }
              </div>
            </div>
          }
        </div>

        <!-- Information Sections -->
        @if (data.entry.uiState.cardInfo?.information?.length > 0) {
          <div class="space-y-6">
            <!-- Experience Section -->
            @if (getExperienceInfo(); as experience) {
              <div class="bg-card rounded-lg border border-gray-200 dark:border-gray-700 p-5">
                <h4 class="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <mat-icon class="mr-2">work</mat-icon>
                  Werkervaring
                </h4>
                <div class="text-foreground leading-relaxed whitespace-pre-wrap">{{ experience }}</div>
              </div>
            }

            <!-- Other Information -->
            @for (info of getOtherInfo(); track info.key) {
              <div class="bg-card rounded-lg border border-gray-200 dark:border-gray-700 p-5">
                <h4 class="text-lg font-semibold text-foreground mb-3 flex items-center">
                  @if (info.icon) {
                    <mat-icon class="text-gray-600 dark:text-gray-400 mr-2">{{ info.icon }}</mat-icon>
                  }
                  {{ info.label }}
                </h4>
                <div class="text-foreground">{{ info.value }}</div>
              </div>
            }
          </div>
        }
      </div>

      <!-- Actions -->
      <div
        class="flex justify-end p-6 pt-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-b-lg">
        <button
          mat-button
          (click)="close()"
          class="text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100">
          Sluiten
        </button>
      </div>
    </div>
  `,
  imports: [MatDialogModule, MatButtonModule, MatIconModule, MatDividerModule, MatChipsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserDetailModalComponent {
  private dialogRef = inject(MatDialogRef<UserDetailModalComponent>);
  protected data = inject<DialogData>(MAT_DIALOG_DATA);

  close(): void {
    this.dialogRef.close();
  }

  /**
   * Get rating information from the data
   */
  getRatingInfo(): { rating: string; amount: string } | null {
    const info = this.data.entry.uiState.cardInfo?.information;
    if (!info) return null;

    const ratingInfo = info.find((item: InformationItem) => item.key === 'average-rating');
    const amountInfo = info.find((item: InformationItem) => item.key === 'ratings-amount');

    if (ratingInfo) {
      return {
        rating: ratingInfo.value || '-',
        amount: amountInfo?.value || '-',
      };
    }
    return null;
  }

  /**
   * Get rate information from the data
   */
  getRateInfo(): string | null {
    const info = this.data.entry.uiState.cardInfo?.information;
    if (!info) return null;

    const rateInfo = info.find((item: InformationItem) => item.key === 'rate');
    return rateInfo ? rateInfo.value : null;
  }

  /**
   * Get competencies information from the data
   */
  getCompetenciesInfo(): CompetencyItem[] | null {
    const info = this.data.entry.uiState.cardInfo?.information;
    if (!info) return null;

    const competenciesInfo = info.find((item: InformationItem) => item.key === 'competencies');

    if (competenciesInfo && Array.isArray(competenciesInfo.value)) {
      // Filter out archived competencies
      return competenciesInfo.value.filter((comp: CompetencyItem) => !comp.archived);
    }

    return null;
  }

  /**
   * Get experience information from the data
   */
  getExperienceInfo(): string | null {
    const info = this.data.entry.uiState.cardInfo?.information;
    if (!info) return null;

    const experienceInfo = info.find((item: InformationItem) => item.key === 'experience');
    console.log('Experience Info:', experienceInfo);
    return experienceInfo ? experienceInfo.value : null;
  }

  /**
   * Get other information excluding the special sections
   */
  getOtherInfo(): InformationItem[] {
    const info = this.data.entry.uiState.cardInfo?.information;
    if (!info) return [];

    const excludedKeys = ['average-rating', 'ratings-amount', 'rate', 'competencies', 'experience'];
    return info.filter((item: InformationItem) => !excludedKeys.includes(item.key) && item.value);
  }
}
